import type { DeckOrder } from "./deck.js";

export interface CollectionSymbol {
  id: number;
  name: string;
  image: string;
}

export interface ImageCrop {
  zoom: number;
  x: number;
  y: number;
}

export interface EditableSymbol extends CollectionSymbol {
  /** Built-in illustration identity, retained locally for reuse without duplicates. */
  assetKey?: string;
  source?: string;
  crop?: ImageCrop;
}

export type CardBack =
  | { kind: "logo"; color: string }
  | { kind: "image"; image: string; color: string; source?: string; crop?: ImageCrop };

export interface CardCollection {
  id: string;
  name: string;
  order: DeckOrder;
  symbols: EditableSymbol[];
  back: CardBack;
  createdAt: number;
  updatedAt: number;
}

export const DEFAULT_CARD_BACK: Readonly<CardBack> = Object.freeze({ kind: "logo", color: "#ed374b" });
export const MAX_COLLECTION_IMAGE_BYTES = 200 * 1024;
export const MAX_COLLECTION_SOURCE_BYTES = 1024 * 1024;
export const MAX_NETWORK_COLLECTION_BYTES = 12 * 1024 * 1024;
export const MAX_COLLECTION_FILE_BYTES = 32 * 1024 * 1024;
export const MAX_COLLECTION_SYMBOLS = 57;

export const COLLECTION_ERRORS = {
  invalid: "Ce fichier ne contient pas un jeu Virou valide.",
  order: "La taille du jeu doit être 13, 31 ou 57 images.",
  name: "Donne un nom de 1 à 60 caractères à ton jeu.",
  symbolName: "Chaque image doit avoir un nom de 1 à 60 caractères.",
  symbolId: "Les identifiants des images doivent être uniques et se suivre à partir de zéro.",
  image: "Utilise une image PNG, JPEG ou WebP valide.",
  imageSize: "Chaque image doit peser au maximum 200 Ko après recadrage.",
  sourceSize: "L’image d’origine doit peser au maximum 1 Mo.",
  crop: "Le recadrage de cette image est invalide.",
  back: "Choisis une couleur et un dos de carte valides.",
  networkSize: "Ce jeu est trop volumineux pour être partagé. Réduis la taille des images.",
  fileSize: "Le fichier du jeu doit peser au maximum 32 Mo.",
  format: "Ce format de fichier Virou n’est pas pris en charge.",
} as const;

export class CollectionValidationError extends Error {
  readonly errors: string[];
  constructor(errors: readonly string[]) {
    super(errors[0] ?? COLLECTION_ERRORS.invalid);
    this.name = "CollectionValidationError";
    this.errors = [...errors];
  }
}

export function collectionSymbolCount(order: DeckOrder): number {
  if (![3, 5, 7].includes(order)) throw new CollectionValidationError([COLLECTION_ERRORS.order]);
  return order * order + order + 1;
}

/** The browser editor, JSON importer and server accept the same local raster format. */
export function isCollectionImage(value: unknown, maxBytes = MAX_COLLECTION_IMAGE_BYTES): value is string {
  if (typeof value !== "string" || value.length > Math.ceil(maxBytes / 3) * 4 + 40) return false;
  const match = /^data:image\/(png|jpeg|webp);base64,([A-Za-z0-9+/]+={0,2})$/.exec(value);
  if (!match) return false;
  const encoded = match[2];
  if (encoded.length % 4 !== 0) return false;
  const bytes = encoded.length / 4 * 3 - (encoded.endsWith("==") ? 2 : encoded.endsWith("=") ? 1 : 0);
  if (bytes === 0 || bytes > maxBytes) return false;
  try {
    const data = atob(encoded);
    if (btoa(data) !== encoded) return false;
    if (match[1] === "png") return data.startsWith("\x89PNG\r\n\x1a\n");
    if (match[1] === "jpeg") return data.startsWith("\xff\xd8\xff");
    return data.startsWith("RIFF") && data.slice(8, 12) === "WEBP";
  } catch {
    return false;
  }
}

function record(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return (prototype === Object.prototype || prototype === null)
    && !["__proto__", "prototype", "constructor"].some((key) => Object.hasOwn(value, key));
}

function exceedsImageLimit(value: unknown, maxBytes: number): boolean {
  if (typeof value !== "string") return false;
  const comma = value.indexOf(",");
  const length = value.length - comma - 1;
  return comma >= 0 && length / 4 * 3 - (value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0) > maxBytes;
}

function validName(value: unknown): value is string {
  return typeof value === "string" && value.trim().length >= 1 && value.trim().length <= 60 && !/[\u0000-\u001f\u007f]/.test(value);
}

function crop(value: unknown): ImageCrop | undefined {
  if (!record(value) || !["zoom", "x", "y"].every((key) => typeof value[key] === "number" && Number.isFinite(value[key]))) return undefined;
  const { zoom, x, y } = value as unknown as ImageCrop;
  return zoom >= 1 && zoom <= 5 && Math.abs(x) <= 1 && Math.abs(y) <= 1 ? { zoom, x, y } : undefined;
}

export interface CollectionValidation {
  valid: boolean;
  errors: string[];
  collection?: CardCollection;
}

export function validateCollection(
  value: unknown,
  { playable = false, includeSources = true }: { playable?: boolean; includeSources?: boolean } = {},
): CollectionValidation {
  const errors: string[] = [];
  const fail = (error: string) => { if (!errors.includes(error)) errors.push(error); };
  if (!record(value)) return { valid: false, errors: [COLLECTION_ERRORS.invalid] };
  if (typeof value.id !== "string" || !/^[A-Za-z0-9_-]{1,80}$/.test(value.id)) fail(COLLECTION_ERRORS.invalid);
  if (!validName(value.name)) fail(COLLECTION_ERRORS.name);
  if (![3, 5, 7].includes(value.order as number)) fail(COLLECTION_ERRORS.order);
  const order = value.order as DeckOrder;
  const expectedCount = [3, 5, 7].includes(order) ? collectionSymbolCount(order) : 0;
  if (!Number.isSafeInteger(value.createdAt) || !Number.isSafeInteger(value.updatedAt)
    || (value.createdAt as number) < 0 || (value.updatedAt as number) < (value.createdAt as number)) fail(COLLECTION_ERRORS.invalid);

  const symbols: EditableSymbol[] = [];
  const ids = new Set<number>();
  if (!Array.isArray(value.symbols) || value.symbols.length > MAX_COLLECTION_SYMBOLS) {
    fail(COLLECTION_ERRORS.invalid);
  } else {
    if (value.symbols.length > expectedCount || (playable && value.symbols.length !== expectedCount)) {
      fail(`Ce jeu doit contenir ${expectedCount} images pour être joué.`);
    }
    for (const item of value.symbols) {
      if (!record(item)) { fail(COLLECTION_ERRORS.invalid); continue; }
      if (!Number.isInteger(item.id) || (item.id as number) < 0 || (item.id as number) >= value.symbols.length || ids.has(item.id as number)) fail(COLLECTION_ERRORS.symbolId);
      ids.add(item.id as number);
      if (!validName(item.name)) fail(COLLECTION_ERRORS.symbolName);
      if (!isCollectionImage(item.image)) fail(exceedsImageLimit(item.image, MAX_COLLECTION_IMAGE_BYTES) ? COLLECTION_ERRORS.imageSize : COLLECTION_ERRORS.image);
      const assetKey = item.assetKey;
      if (assetKey !== undefined && (typeof assetKey !== "string" || !/^(classic2?:[a-z-]{1,40}|nautico:\d{1,2})$/.test(assetKey))) fail(COLLECTION_ERRORS.invalid);
      let original: string | undefined;
      let imageCrop: ImageCrop | undefined;
      if (item.source !== undefined) {
        if (!isCollectionImage(item.source, MAX_COLLECTION_SOURCE_BYTES)) fail(exceedsImageLimit(item.source, MAX_COLLECTION_SOURCE_BYTES) ? COLLECTION_ERRORS.sourceSize : COLLECTION_ERRORS.image);
        else if (includeSources) original = item.source;
      }
      if (item.crop !== undefined) {
        imageCrop = crop(item.crop);
        if (!imageCrop) fail(COLLECTION_ERRORS.crop);
      }
      symbols.push({ id: item.id as number, name: typeof item.name === "string" ? item.name.trim() : "", image: item.image as string,
        ...(original === undefined ? {} : { source: original }), ...(includeSources && imageCrop ? { crop: imageCrop } : {}),
        ...(includeSources && typeof assetKey === "string" ? { assetKey } : {}) });
    }
  }

  let back: CardBack = { ...DEFAULT_CARD_BACK };
  if (!record(value.back) || typeof value.back.color !== "string" || !/^#[0-9a-f]{6}$/i.test(value.back.color) || !["logo", "image"].includes(value.back.kind as string)) fail(COLLECTION_ERRORS.back);
  else if (value.back.kind === "logo") back = { kind: "logo", color: value.back.color as string };
  else {
    if (!isCollectionImage(value.back.image)) fail(COLLECTION_ERRORS.image);
    const original = value.back.source;
    if (original !== undefined && !isCollectionImage(original, MAX_COLLECTION_SOURCE_BYTES)) fail(COLLECTION_ERRORS.image);
    const imageCrop = value.back.crop === undefined ? undefined : crop(value.back.crop);
    if (value.back.crop !== undefined && !imageCrop) fail(COLLECTION_ERRORS.crop);
    back = { kind: "image", color: value.back.color as string, image: value.back.image as string,
      ...(includeSources && original !== undefined ? { source: original as string } : {}), ...(includeSources && imageCrop ? { crop: imageCrop } : {}) };
  }

  if (errors.length) return { valid: false, errors };
  const collection: CardCollection = {
    id: value.id as string, name: (value.name as string).trim(), order, symbols: symbols.sort((a, b) => a.id - b.id), back,
    createdAt: value.createdAt as number, updatedAt: value.updatedAt as number,
  };
  const encodedSize = new TextEncoder().encode(JSON.stringify(collection)).byteLength;
  if (encodedSize > MAX_COLLECTION_FILE_BYTES) fail(COLLECTION_ERRORS.fileSize);
  if (playable) {
    const networkValue = { ...collection, symbols: collection.symbols.map(({ id, name, image }) => ({ id, name, image })), back: stripBackSources(collection.back) };
    if (new TextEncoder().encode(JSON.stringify(networkValue)).byteLength > MAX_NETWORK_COLLECTION_BYTES) fail(COLLECTION_ERRORS.networkSize);
  }
  return errors.length ? { valid: false, errors } : { valid: true, errors: [], collection };
}

function stripBackSources(back: CardBack): CardBack {
  return back.kind === "logo" ? { kind: "logo", color: back.color } : { kind: "image", color: back.color, image: back.image };
}

export function toPlayableCollection(value: CardCollection): CardCollection {
  const result = validateCollection(value, { playable: true, includeSources: false });
  if (!result.valid || !result.collection) throw new CollectionValidationError(result.errors);
  return result.collection;
}

export function isPlayableCollection(value: unknown): value is CardCollection {
  return validateCollection(value, { playable: true, includeSources: false }).valid;
}

export function newCollectionId(): string {
  // randomUUID is restricted to secure contexts; LAN development also works
  // over HTTP, where getRandomValues remains available.
  if (typeof crypto.randomUUID === "function") return `jeu_${crypto.randomUUID()}`;
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, "0")).join("");
  return `jeu_${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export interface CollectionFile {
  format: "virou-collection";
  version: 1;
  collection: CardCollection;
}

/** Exporting a draft is allowed so unfinished artwork can be backed up. */
export function exportCollection(value: CardCollection): string {
  const result = validateCollection(value);
  if (!result.valid || !result.collection) throw new CollectionValidationError(result.errors);
  const envelope: CollectionFile = { format: "virou-collection", version: 1, collection: result.collection };
  const text = JSON.stringify(envelope);
  if (new TextEncoder().encode(text).byteLength > MAX_COLLECTION_FILE_BYTES) throw new CollectionValidationError([COLLECTION_ERRORS.fileSize]);
  return text;
}

/** Importing always creates a new local identity, including repeated imports. */
export function importCollection(text: string): CardCollection {
  if (new TextEncoder().encode(text).byteLength > MAX_COLLECTION_FILE_BYTES) throw new CollectionValidationError([COLLECTION_ERRORS.fileSize]);
  let value: unknown;
  try { value = JSON.parse(text); } catch { throw new CollectionValidationError([COLLECTION_ERRORS.invalid]); }
  if (!record(value) || value.format !== "virou-collection" || value.version !== 1) throw new CollectionValidationError([COLLECTION_ERRORS.format]);
  const result = validateCollection(value.collection);
  if (!result.valid || !result.collection) throw new CollectionValidationError(result.errors);
  const now = Date.now();
  return { ...result.collection, id: newCollectionId(), createdAt: now, updatedAt: now };
}
